import { Text } from "@chakra-ui/react";
import { forwardRef, type ReactNode } from "react";

/**
 * PreHeader — Geist Mono UPPERCASE micro-label.
 *
 * Used above every Card title, KPI value, and section to introduce content
 * with editorial weight. The single biggest unifying signal across the soft-
 * editorial design system.
 *
 * Style is fixed by `theme/typography.ts` → `typography.preHeader`.
 *
 * @example
 *   <PreHeader>Activity · Last 14 days</PreHeader>
 *   <Heading size="md">Messages</Heading>
 */

interface PreHeaderProps {
  children: ReactNode;
  /** Override colour. Defaults to secondary text. */
  color?: string;
  /**
   * Pre-header size.
   *  - "md" (default) → brand-canonical 14px / 1.40px tracking. Use above
   *    KPI values, page titles, hero sections.
   *  - "sm" → 13px, proportional 0.10em tracking. Section headers inside cards.
   *  - "xs" → 11px, proportional 0.10em tracking. Dense table/cell labels.
   * All sizes preserve the brand 0.10em (= 1.40px at 14px) tracking ratio.
   */
  size?: "xs" | "sm" | "md";
  className?: string;
}

// Brand spec: Geist Mono Medium 14px / 1.40px tracking (= 0.10em at 14px).
// Smaller sizes preserve the same 0.10em ratio so the rhythm scales cleanly.
const sizeMap = {
  xs: { fontSize: "11px", letterSpacing: "0.10em" },
  sm: { fontSize: "13px", letterSpacing: "0.10em" },
  md: { fontSize: "14px", letterSpacing: "0.10em" },
};

export const PreHeader = forwardRef<HTMLParagraphElement, PreHeaderProps>(
  function PreHeader({ children, color, size = "md", className }, ref) {
    const sz = sizeMap[size];
    return (
      <Text
        ref={ref}
        as="p"
        className={className}
        fontFamily="var(--font-mono)"
        fontWeight={500}
        textTransform="uppercase"
        lineHeight="1"
        color={color ?? "var(--text-secondary-color)"}
        {...sz}
      >
        {children}
      </Text>
    );
  },
);
